import { useCallback, useMemo } from 'react';

import { useQuery, UseQueryResult } from '@tanstack/react-query';

import {
	buildLabelsExcludedFromDashboard,
	processPlatformConfiguration
} from 'src/apps/dashboard/helpers/ConfigurationProcessingHelpers';
import { getMobileSdkTests } from 'src/apps/dashboard/services/mobileDashboardService';
import { TMobileSdkTestsResult } from 'src/apps/dashboard/types/MobileDashboardServiceTypes';
import { REFETCH_INTERVAL } from 'src/common/consts/QueryConfigs';
import { useConfiguration } from 'src/common/providers/configurationProvider/ConfigurationProvider';
import { DASHBOARD_MOBILE_SDK_TESTS } from 'src/common/providers/reactQueryProvider/QueryKeys';
import { queryClient } from 'src/common/providers/reactQueryProvider/ReactQueryProvider';
import { IUseTableQueryParams } from 'src/common/types/UseTableQueryParamsType';

interface IUseMobileSdkTestsQueryParams extends IUseTableQueryParams {
	dateRange?: string;
}

export const useMobileSdkTestsQuery = ({
	dateRange,
	filters,
	sort,
	page,
	pageSize
}: IUseMobileSdkTestsQueryParams): [UseQueryResult<TMobileSdkTestsResult>, () => void] => {
	const { configuration } = useConfiguration();
	const labelsExcludedFromDashboard = useMemo(() => {
		const { mobileCoreLabels } = processPlatformConfiguration(configuration?.PLATFORM);
		return buildLabelsExcludedFromDashboard(mobileCoreLabels);
	}, [configuration]);

	const queryResult = useQuery(
		[
			DASHBOARD_MOBILE_SDK_TESTS,
			filters,
			sort,
			page,
			pageSize,
			dateRange,
			labelsExcludedFromDashboard
		],
		async (): Promise<TMobileSdkTestsResult> =>
			getMobileSdkTests({
				dateRange,
				filterOptions: filters,
				sort,
				page,
				pageSize,
				labelsExcludedFromDashboard
			}),
		{
			keepPreviousData: true,
			refetchInterval: REFETCH_INTERVAL,
			staleTime: REFETCH_INTERVAL
		}
	);

	const invalidate = useCallback(
		() =>
			queryClient.invalidateQueries({
				queryKey: [DASHBOARD_MOBILE_SDK_TESTS],
				refetchType: 'none'
			}),
		[]
	);

	return [queryResult, invalidate];
};
